import {
  type AppraisalFiltersState,
  type SavedAppraisalSearch,
  buildAppraisalFilterPredicate,
  deserializeAppraisalCriteria,
  getDefaultAppraisalFilters,
  hasActiveAppraisalCriteria,
  serializeAppraisalCriteria,
} from '@/components/ui/appraisal-list/appraisal-filter-state.ts';
import AppraisalFilters from '@/components/ui/appraisal-list/appraisal-filters.tsx';
import VirtualizedAppraisalList from '@/components/ui/appraisal-list/virtualized-appraisal-list.tsx';
import InlineTextInput from '@/components/ui/inline-text-input.tsx';
import { Separator } from '@/components/ui/separator.tsx';
import useAppraisalFindInViewFilterCallback from '@/hooks/use-appraisal-find-in-view.ts';
import useLocalStorage from '@/hooks/use-local-storage.ts';
import { appraisalStoreV2 } from '@/models/stores/appraisals-v2.ts';
import { createFileRoute } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';

const SAVED_APPRAISAL_SEARCHES_KEY = 'appraisals:saved-searches:v1';

const AppraisalsPage = observer(() => {
  const appraisals = appraisalStoreV2.allByMostRecent;

  const { findInViewFilterCallback, findInViewQuery, setFindInViewQuery } =
    useAppraisalFindInViewFilterCallback(appraisals);

  const [filters, setFilters] = useState<AppraisalFiltersState>(() =>
    getDefaultAppraisalFilters(),
  );
  const [savedSearches, setSavedSearches] = useLocalStorage<
    SavedAppraisalSearch[]
  >(SAVED_APPRAISAL_SEARCHES_KEY, []);

  const filtersPredicate = useMemo(
    () => buildAppraisalFilterPredicate(filters),
    [filters],
  );

  const combinedPredicate = useMemo(() => {
    return (a: any) => {
      return filtersPredicate(a) && findInViewFilterCallback(a);
    };
  }, [filtersPredicate, findInViewFilterCallback]);

  const hasActiveCriteria = hasActiveAppraisalCriteria({
    filters,
    findInViewQuery,
  });

  const clearCriteria = useCallback(() => {
    setFilters(getDefaultAppraisalFilters());
    setFindInViewQuery('');
  }, [setFindInViewQuery]);

  const saveCurrentSearch = useCallback(() => {
    const fallbackName =
      findInViewQuery.trim() || `Appraisals Search ${savedSearches.length + 1}`;
    const name = window.prompt('Name this search', fallbackName);
    const trimmedName = name?.trim();
    if (!trimmedName) return;

    const now = new Date().toISOString();
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}`;

    const nextSearch: SavedAppraisalSearch = {
      id,
      name: trimmedName,
      criteria: serializeAppraisalCriteria({
        filters,
        findInViewQuery,
      }),
      createdAt: now,
      updatedAt: now,
    };

    setSavedSearches((current) => [nextSearch, ...current]);
  }, [filters, findInViewQuery, savedSearches.length, setSavedSearches]);

  const applySavedSearch = useCallback(
    (search: SavedAppraisalSearch) => {
      try {
        const criteria = deserializeAppraisalCriteria(search.criteria);
        setFilters(criteria.filters);
        setFindInViewQuery(criteria.findInViewQuery);
      } catch {
        setSavedSearches((current) =>
          current.filter((savedSearch) => savedSearch.id !== search.id),
        );
      }
    },
    [setFindInViewQuery, setSavedSearches],
  );

  const deleteSavedSearch = useCallback(
    (id: string) => {
      setSavedSearches((current) =>
        current.filter((search) => search.id !== id),
      );
    },
    [setSavedSearches],
  );

  return (
    <div>
      <InlineTextInput
        className="ml-2 p-1 bg-gray-100 border mb-2 w-64"
        placeholder="Find in view..."
        value={findInViewQuery}
        onChange={(e) => setFindInViewQuery(e.target.value)}
        autoFocus
      />

      <AppraisalFilters
        data={appraisals}
        state={filters}
        onChange={setFilters}
        onClear={clearCriteria}
        clearDisabled={!hasActiveCriteria}
        saveDisabled={!hasActiveCriteria}
        savedSearches={savedSearches}
        onSaveSearch={saveCurrentSearch}
        onApplySavedSearch={applySavedSearch}
        onDeleteSavedSearch={deleteSavedSearch}
      />

      <Separator />

      <VirtualizedAppraisalList
        appraisalData={appraisals}
        filterCallback={combinedPredicate}
        sort={filters.sort}
        enableScope={() => true}
      />
    </div>
  );
});

export const Route = createFileRoute('/(authenticated)/appraisals/')({
  component: AppraisalsPage,
  staticData: {
    keepAlive: true,
  },
});
